import * as Yup from 'yup';
import { DateTime } from 'luxon';

import AddRepair from './Components/AddRepair';
import Repairs from './Components/Repairs';

import SelectCustomer from './Components/SelectCustomer';
import SelectWig from './Components/SelectWig';
import SelectRep from './Components/SelectRep';

import OpenRepairs from './Components/OpenRepairs';

import AllInclusiveRoundedIcon from '@mui/icons-material/AllInclusiveRounded';
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';

import SimpleChip from '../../utils/utilities/SimpleChip';

import { displayLabelService, displayLabelCustomer } from '../../utils/displayLabels';


import SelectDueDate from './Components/DueDate';


const headers = [
  {
    label: 'Ticket ID',
    id: 'repair_ticket_num'
  },
  {
    label: "Repair",
    id: "repair_name",
    render: row => <SimpleChip label={row.repair_name} />
  },
  {
    label: 'Customer',
    id: 'client_name',
    render: (row) => {
      let option = {
        full_name: row.client_name,
        mobile_phone: row.customer_phone,
      }
      return displayLabelCustomer(option, "short")
    }
  },
  {
    label: 'Wig ID',
    id: 'wig_id'
  },
  {
    label: 'Status',
    id: 'status',
    multi: true,
    values: [{value: "scheduled", label: "Scheduled", status: "primary"}, {value: "prepping", label: "In Prep", status: "warning"}, {value: "in_progress", label: "In Progress", status: "warning"}, {value: "completed", status: "success"}]
  },
  // {
  //   label: "Price",
  //   id: "total",
  //   currency: true
  // },
  {
    label: "Due Date",
    id: "due_date",
    dateShort: true
  },
  {
    label: "Appointment",
    id: "scheduled_appointment",
    dateShort: true
  },
  {
    label: "Specialist",
    id: "specialist_name",
    render: row => row.specialist_name || row.vendor_name
  },
  {
    label: "Service",
    id: "in_house",
    multi: true,
    values: [{label: "In House", value: true, status: "success"}, {label: "External", value: false, status: "info"}]
  },
  // {
  //   label: 'Appt Date',
  //   id: 'appointment_date',
  //   date: true
  // }
  {
    label: "Group",
    id: "number_of_repairs",
    render: row => `${row.current_repair_order} / ${row.number_of_repairs}`
  }
];

const filters = [
  {
    id: "scheduled_appointment",
    type: "date",
    label: "Scheduled Appointment",
  },
  {
    id: "estimated_date_of_completion",
    type: "date",
    label: "ETA",
  },
  {
    id: "rt.repair_name",
    type: "autoMulti",
    label: "Repair Name",
    url: '/repairs',
    fieldOptions: {
      fieldName: "name",
      displayField: "name"
    }
  },
  {
    id: "client_name",
    type: "autoMulti",
    label: "Customer",
    url: '/customers',
    fieldOptions: {
      fieldName: "full_name",
      displayField: "full_name"
    }
  },
  {
    id: "rt.in_house",
    type: "boolean",
    label: "In House Repair",
    values: [{value: true, label: "In House", status: "success"}, {value: false, label: "External", status: "info"}]
  },
  {
    id: "s.full_name",
    type: "autoMulti",
    label: "Specialist",
    url: '/specialists',
    fieldOptions: {
      fieldName: "full_name",
      displayField: "full_name"
    },
    prefetch: true
  },
  {
    id: "v.full_name",
    type: "autoMulti",
    label: "Repair Vendors",
    url: '/vendors?does_repairs[$eq]=true',
    fieldOptions: {
      fieldName: "full_name",
      displayField: "full_name"
    },
    prefetch: true
  }
];

const tabs = [
  {id: "all", label: "All", Icon: AllInclusiveRoundedIcon },
  {id: "scheduled", label: "Scheduled", Icon: ScheduleIcon },
  {id: "prepping", label: "In Prep", Icon: AutoFixHighRoundedIcon },
  {id: "in_progress", label: "In Progress", Icon: HourglassEmptyIcon },
  {id: "completed", label: "Completed", Icon: TaskAltIcon },
]

const fields = [
  { 
    fieldType: "Component",
    Component: <SelectCustomer />,
    name: "select_customer",
    size: {xs: 12, lg: 12},
  },
  {
   fieldType: "Component",
   Component: <SelectRep />,
   name: "select_rep",
   size: {xs: 12, lg: 12}, 
  },
  {
    fieldType: "divider",
    header: "Wig Details",
    name: "wig_details",
    size: {xs: 12, lg: 12},
  },
  { 
    fieldType: "Component",
    Component: <SelectWig />,
    name: "select_wig",
    size: {xs: 12, lg: 12},
  },
  // {
  //   fieldType: "Component",
  //   Component: <OpenRepairs />,
  //   name: "open_repairs",
  //   size: {xs: 12, lg: 12},
  // },
  {
    fieldType: "divider",
    header: "Select Repair",
    name: "select_repair",
    size: {xs: 12, lg: 12},
  },
  {
    fieldType: "Component",
    Component: <AddRepair />,
    name: "add_repair",
    size: {xs: 12, lg: 12},
  },
  {
    fieldType: "Component",
    Component: <Repairs />,
    name: "repairs",
    size: {xs: 12, lg: 12},
  },
  {
    fieldType: "divider",
    header: "Schedule Follow Up",
    name: "follow_up",
    size: {xs: 12, lg: 12},
  },
  {
    fieldType: "Component",
    Component: <SelectDueDate />,
    name: "select_due_date",
    size: {xs: 12, lg: 12},
  },
  {
    fieldType: "debounceSearchSolo",
    formFieldType: "select",
    select: [],
    name: "follow_up_service",
    label: "Select Follow-Up Service",
    url: "services",
    fieldName: "name",
    displayField: "name",
    displayLabel: displayLabelService,
    // FormComponent: <EditCustomer dialog={true}/>,
    size: {xs: 12, lg: 12},
  }
]

const initialValues = {
  customer_id: '',
  stylist_rep: '',
  wig_source: 'inventory',
  wig_id: '',
  repairs: [],
  media: [],
  media_urls: [],
  due_date: "",
}


const additionalState = {
  customer_name: '',
  stylist_name: '',
  vendor_name: '',
  customer_phone: '',
  wig_sold_on: DateTime.now(),
  inventory_id: '',
  sku: '',
}

const schema = Yup.object().shape({
 customer_id: Yup.string().required('Customer is required'),
 stylist_rep: Yup.string().nullable(),
 wig_id: Yup.string().required('Wig is required'),
 wig_source: Yup.string().required('Wig Source is required'),
 repairs: Yup.array(Yup.mixed()).min(1, 'At least one repair must be added').required('Repair list is required'),
});


export { fields, initialValues, schema, additionalState, headers, filters, tabs 
  // parseData,
 };